import { db, batchTransact, transactWithRetry } from '@/lib/db'
import { id } from '@instantdb/react'
import type { Asset, AssetAnalysisStatus } from '@/types'

/**
 * 素材管理 Hook
 * 提供素材的增删改查功能
 */
export function useAssets(projectId: string) {
  // 查询项目下的所有素材
  const { data, isLoading, error } = db.useQuery({
    assets: {
      $: {
        where: { project_id: projectId },
      },
    },
  })

  const assets = (data?.assets || []) as Asset[]

  // 添加素材
  const addAsset = async (params: {
    filename: string
    url: string
    thumbnail_url?: string
    title?: string
    description?: string
    tags?: string[]
    type?: 'raw' | 'combined' | 'ai_generated'
    analysis_status?: AssetAnalysisStatus
    folder_id?: string | null
  }) => {
    const assetId = id()
    await transactWithRetry(
      db.tx.assets[assetId].update({
        id: assetId,
        project_id: projectId,
        filename: params.filename,
        url: params.url,
        thumbnail_url: params.thumbnail_url || params.url,
        title: params.title || '',
        description: params.description || '',
        tags: params.tags || [],
        type: params.type || 'raw',
        analysis_status: params.analysis_status || 'pending',
        folder_id: params.folder_id || null,
        created_at: Date.now(),
      })
    )
    return assetId
  }

  // 批量添加素材（返回创建的素材ID列表，用于后续分析）
  const addAssets = async (
    items: Array<{
      filename: string
      url: string
      thumbnail_url?: string
      title?: string
      analysis_status?: AssetAnalysisStatus
      folder_id?: string | null
    }>
  ): Promise<Array<{ id: string; filename: string; url: string }>> => {
    const assetRecords: Array<{ id: string; filename: string; url: string }> = []
    const txns = items.map((item) => {
      const assetId = id()
      assetRecords.push({ id: assetId, filename: item.filename, url: item.url })
      return db.tx.assets[assetId].update({
        id: assetId,
        project_id: projectId,
        filename: item.filename,
        url: item.url,
        thumbnail_url: item.thumbnail_url || item.url,
        title: item.title || '',
        description: '',
        tags: [],
        type: 'raw',
        analysis_status: item.analysis_status || 'analyzing',
        folder_id: item.folder_id || null,
        created_at: Date.now(),
      })
    })
    await batchTransact(txns)
    return assetRecords
  }

  // 更新素材分析状态
  const updateAnalysisStatus = async (
    assetId: string,
    status: AssetAnalysisStatus
  ) => {
    await transactWithRetry(db.tx.assets[assetId].update({ analysis_status: status }))
  }

  // 批量更新素材分析状态
  const updateMultipleAnalysisStatus = async (
    assetIds: string[],
    status: AssetAnalysisStatus
  ) => {
    const txns = assetIds.map((assetId) => 
      db.tx.assets[assetId].update({ analysis_status: status })
    )
    await batchTransact(txns)
  }

  // 批量更新素材分析结果
  const updateAssetsAnalysis = async (
    results: Array<{
      id: string
      description: string
      title?: string
      tags: string[]
      scene?: string
      mood?: string
      composition?: string[]
      pure_hue?: string
      tonality?: string
    }>
  ) => {
    const txns = results.map((result) =>
      db.tx.assets[result.id].update({
        description: result.description,
        title: result.title,
        tags: result.tags,
        scene: result.scene,
        mood: result.mood,
        composition: result.composition,
        pure_hue: result.pure_hue,
        tonality: result.tonality,
        analysis_status: 'completed' as AssetAnalysisStatus,
      })
    )
    await batchTransact(txns)
  }

  // 更新素材
  const updateAsset = async (
    assetId: string,
    updates: Partial<Omit<Asset, 'id' | 'project_id' | 'created_at'>>
  ) => {
    await transactWithRetry(db.tx.assets[assetId].update(updates))
  }

  // 删除素材
  const deleteAsset = async (assetId: string) => {
    await transactWithRetry(db.tx.assets[assetId].delete())
  }

  // 批量删除素材
  const deleteAssets = async (assetIds: string[]) => {
    const txns = assetIds.map((assetId) => db.tx.assets[assetId].delete())
    await batchTransact(txns)
  }

  // 移动素材到文件夹
  const moveAssetsToFolder = async (assetIds: string[], folderId: string | null) => {
    const txns = assetIds.map((assetId) => 
      db.tx.assets[assetId].update({ folder_id: folderId || null })
    )
    await batchTransact(txns)
  }

  // 标记素材被某笔记使用
  const markAssetsUsed = async (assetIds: string[], noteId: string) => {
    if (assetIds.length === 0) return
    const txns = assetIds.map((assetId) => {
      const asset = assets.find(a => a.id === assetId)
      const currentUsedIds = asset?.usedInNoteIds || []
      // 避免重复添加
      if (currentUsedIds.includes(noteId)) return null
      return db.tx.assets[assetId].update({
        usedInNoteIds: [...currentUsedIds, noteId]
      })
    }).filter(Boolean)
    if (txns.length > 0) {
      await batchTransact(txns)
    }
  }

  // 释放素材（从 usedInNoteIds 中移除 noteId）
  const releaseAssetsFromNote = async (noteId: string) => {
    // 找出所有被该笔记使用的素材
    const usedAssets = assets.filter(a => a.usedInNoteIds?.includes(noteId))
    if (usedAssets.length === 0) return
    
    const txns = usedAssets.map((asset) => {
      const updatedIds = (asset.usedInNoteIds || []).filter(id => id !== noteId)
      return db.tx.assets[asset.id].update({
        usedInNoteIds: updatedIds
      })
    })
    await batchTransact(txns)
  }

  // 重置项目下所有素材的使用状态
  const resetAllAssetUsage = async () => {
    if (assets.length === 0) return
    const txns = assets.map((asset) => 
      db.tx.assets[asset.id].update({ usedInNoteIds: [] })
    )
    await batchTransact(txns)
  }

  return {
    assets,
    isLoading,
    error,
    addAsset,
    addAssets,
    updateAsset,
    updateAnalysisStatus,
    updateMultipleAnalysisStatus,
    updateAssetsAnalysis,
    deleteAsset,
    deleteAssets,
    moveAssetsToFolder,
    markAssetsUsed,
    releaseAssetsFromNote,
    resetAllAssetUsage,
  }
}

